import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { X, Save, Film } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/Button';
import { Input, Textarea } from '../ui/Input';

const DIFFICULTIES = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];
const selectCls = 'h-11 w-full rounded-xl border border-zinc-800 bg-ink-surface px-3 text-sm text-white outline-none focus:border-zinc-600';

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-zinc-400">{label}</label>
      {children}
      {hint && <p className="mt-1 text-[11px] text-zinc-600">{hint}</p>}
    </div>
  );
}

export function LessonEditor({ lesson, courses = [], defaultCourseId = '', defaultSectionId = '', onClose, onSaved }) {
  const isEdit = !!lesson;
  const [busy, setBusy] = useState(false);
  const [sections, setSections] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState(lesson?.tags || []);
  const [form, setForm] = useState({
    title: lesson?.title || '',
    description: lesson?.description || '',
    difficulty: lesson?.difficulty || 'Beginner',
    thumbnail_url: lesson?.thumbnail_url || '',
    bunny_video_id: lesson?.bunny_video_id || '',
    video_url: lesson?.video_url || '',
    status: lesson?.status || 'published',
    show_in_discover: lesson?.show_in_discover ?? true,
    course_id: lesson?.course_id || defaultCourseId || '',
    section_id: lesson?.section_id || defaultSectionId || '',
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTag = (name) => setSelectedTags((t) => (t.includes(name) ? t.filter((x) => x !== name) : [...t, name]));

  useEffect(() => {
    api.get('/admin/tags').then((r) => setAllTags(r.data.tags || r.data || [])).catch(() => setAllTags([]));
  }, []);

  useEffect(() => {
    if (!form.course_id) { setSections([]); return; }
    api.get(`/admin/courses/${form.course_id}/sections`)
      .then((r) => setSections(r.data.sections || []))
      .catch(() => setSections([]));
  }, [form.course_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error('Title is required');
    if (!form.bunny_video_id.trim() && !form.video_url.trim()) {
      return toast.error('Add a video ID or a direct video link');
    }
    setBusy(true);
    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      difficulty: form.difficulty,
      tags: selectedTags,
      thumbnail_url: form.thumbnail_url.trim(),
      bunny_video_id: form.bunny_video_id.trim(),
      video_url: form.video_url.trim(),
      status: form.status,
      show_in_discover: !!form.show_in_discover,
      course_id: form.course_id || null,
      section_id: form.section_id || null,
    };
    try {
      if (isEdit) await api.put(`/admin/lessons/${lesson.lesson_id}`, payload);
      else await api.post('/admin/lessons', payload);
      toast.success(isEdit ? 'Lesson updated' : 'Lesson created');
      onSaved?.();
      onClose?.();
    } catch (er) {
      toast.error(er?.response?.data?.detail || 'Could not save lesson');
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto p-4 sm:p-8">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16 }}
          className="relative z-10 w-full max-w-2xl rounded-2xl border border-white/10 bg-ink-card p-6 shadow-2xl"
          data-testid="lesson-editor">
          <div className="mb-5 flex items-center justify-between">
            <h3 className="flex items-center gap-2 font-heading text-lg font-semibold text-white">
              <Film size={18} className="text-emerald" /> {isEdit ? 'Edit lesson' : 'New lesson'}
            </h3>
            <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="lesson-editor-close"><X size={20} /></button>
          </div>

          <div className="grid gap-4">
            <Field label="Title">
              <Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Lesson title" data-testid="lesson-title" />
            </Field>
            <Field label="Description">
              <Textarea rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="What this lesson covers…" data-testid="lesson-description" />
            </Field>

            <div className="rounded-xl border border-emerald/20 bg-emerald/5 p-4">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-emerald">Video</div>
              <Field label="Video ID" hint="Paste your video ID (or the full embed link). Length & thumbnail are pulled in automatically.">
                <Input value={form.bunny_video_id} onChange={(e) => set('bunny_video_id', e.target.value)} placeholder="e.g. 7e9c1f20-3b4a-…" data-testid="lesson-bunny-id" />
              </Field>
              <div className="mt-3">
                <Field label="Direct video link (optional)" hint="Only if not using your hosted video — a direct .mp4 or .m3u8 URL.">
                  <Input value={form.video_url} onChange={(e) => set('video_url', e.target.value)} placeholder="https://…" data-testid="lesson-video-url" />
                </Field>
              </div>
            </div>

            <Field label="Tags" hint={allTags.length ? 'Pick the tags shown next to this lesson.' : 'No tags yet — create them in the Tags tab first.'}>
              <div className="flex flex-wrap gap-2" data-testid="lesson-tags">
                {allTags.map((t) => {
                  const on = selectedTags.includes(t.name);
                  return (
                    <button
                      type="button"
                      key={t.name}
                      onClick={() => toggleTag(t.name)}
                      data-testid={`lesson-tag-${t.name}`}
                      className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${on ? 'border-emerald bg-emerald/15 text-emerald' : 'border-white/10 text-zinc-400 hover:border-white/25'}`}
                    >
                      {t.name}
                    </button>
                  );
                })}
              </div>
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Difficulty">
                <select className={selectCls} value={form.difficulty} onChange={(e) => set('difficulty', e.target.value)} data-testid="lesson-difficulty">
                  {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select className={selectCls} value={form.status} onChange={(e) => set('status', e.target.value)} data-testid="lesson-status">
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </Field>
              <Field label="Thumbnail URL" hint="Leave blank to auto-use the video thumbnail.">
                <Input value={form.thumbnail_url} onChange={(e) => set('thumbnail_url', e.target.value)} placeholder="https://…" data-testid="lesson-thumbnail" />
              </Field>
              <Field label="Course">
                <select className={selectCls} value={form.course_id} onChange={(e) => { set('course_id', e.target.value); set('section_id', ''); }} data-testid="lesson-course">
                  <option value="">— None —</option>
                  {courses.map((c) => <option key={c.course_id} value={c.course_id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Section">
                <select className={selectCls} value={form.section_id} onChange={(e) => set('section_id', e.target.value)} disabled={!form.course_id} data-testid="lesson-section">
                  <option value="">— None —</option>
                  {sections.map((s) => <option key={s.section_id} value={s.section_id}>{s.name}</option>)}
                </select>
              </Field>
            </div>

            <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" checked={form.show_in_discover} onChange={(e) => set('show_in_discover', e.target.checked)} className="h-4 w-4 accent-emerald" data-testid="lesson-discover" />
              Show in Discover
            </label>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="emerald" disabled={busy} data-testid="lesson-save">
              <Save size={16} /> {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create lesson'}
            </Button>
          </div>
        </motion.form>
      </div>
    </AnimatePresence>
  );
}
